import { translateApiErrorMessage } from '@/utils/translateMessage'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSwal } from '@/hooks/useSwal'
import type { Notification } from '@/redux/slices/notificationsSlice'

const ERROR_CATEGORY_MAP: Record<number, string> = {
  400: 'validation',
  401: 'auth',
  403: 'forbidden',
  404: 'notFound',
  408: 'network',
  409: 'conflict',
  422: 'validation',
  429: 'rateLimit',
  500: 'server',
  502: 'server',
  503: 'server',
  504: 'server',
}

interface HandleApiErrorOptions {
  title?: string
  message?: string | null
  onComplete?: ((errorMessage: string, error: any) => void) | null
  silent?: boolean
}

export function useErrorHandler() {
  const { t } = useTranslation()
  const swal = useSwal()

  // Notifications surface directly via useSwal toasts (the project's
  // notification system) — the redux notificationsSlice has no renderer, so
  // dispatching there was invisible to users.
  const addNotif = useCallback(
    (notification: { type: Notification['type']; message: string; title: string; duration?: number }) => {
      const duration = notification.duration !== undefined ? notification.duration : 5000
      const rawTitle = notification.title || ''
      const translatedTitle = rawTitle ? (t(rawTitle) || rawTitle) : ''
      const rawMessage = notification.message || ''
      const translatedMessage = rawMessage ? translateApiErrorMessage(rawMessage, t) : ''
      const failedLabel = t('Failed') || 'Failed'
      const prefix = translatedTitle && translatedTitle !== translatedMessage && translatedTitle !== failedLabel && translatedTitle !== 'Failed' ? `${translatedTitle}: ` : ''
      swal.notify(notification.type, `${prefix}${translatedMessage}`, duration)
    },
    [swal, t]
  )

  const categorize = useCallback((error: any): string => {
    // Axios services carry response.status; RTK Query (.unwrap() rejections)
    // carry a top-level status with the payload under .data.
    const status = error?.response?.status ?? error?.status
    if (!status && error?.code === 'ERR_NETWORK') return 'network'
    if (!status && error?.message?.includes('timeout')) return 'timeout'
    return ERROR_CATEGORY_MAP[status] || 'unknown'
  }, [])

  const showSuccess = useCallback(
    (message: string, title: string = '') => {
      addNotif({ type: 'success', message, title })
    },
    [addNotif]
  )

  const showError = useCallback(
    (message: string, title: string = '') => {
      addNotif({ type: 'error', message, title, duration: 10000 })
    },
    [addNotif]
  )

  const showWarning = useCallback(
    (message: string, title: string = '') => {
      addNotif({ type: 'warning', message, title })
    },
    [addNotif]
  )

  const showInfo = useCallback(
    (message: string, title: string = '') => {
      addNotif({ type: 'info', message, title })
    },
    [addNotif]
  )

  const handleApiError = useCallback(
    (error: any, options: HandleApiErrorOptions | string = {}): { errorMessage: string; category: string } => {
      const opts: HandleApiErrorOptions = typeof options === 'string' ? { title: options } : options

      const {
        title = t('Failed') || 'Failed',
        message = null,
        onComplete = null,
        silent = false,
      } = opts

      const category = categorize(error)

      // Backend contract is { success, message, errors } — reachable via
      // axios (error.response.data) or RTK Query (error.data).
      const payload = error?.response?.data ?? error?.data
      const payloadMessage = payload?.message
      let errorMessage: string

      if (payload?.errors) {
        const formatted = formatErrorMessages(payload.errors).map(msg => translateApiErrorMessage(msg, t))
        errorMessage = formatted.length > 0 ? formatted.join('\n') : (message || payloadMessage || error?.message || error?.error || t('InvalidData') || 'Invalid Data')
      } else if (error?.errors) {
        const formatted = formatErrorMessages(error.errors).map(msg => translateApiErrorMessage(msg, t))
        errorMessage = formatted.length > 0 ? formatted.join('\n') : (message || payloadMessage || error?.message || error?.error || t('InvalidData') || 'Invalid Data')
      } else {
        const raw = message || (typeof payloadMessage === 'string' && payloadMessage ? payloadMessage : (error?.message || error?.error || t('InvalidData') || 'Invalid Data'))
        errorMessage = translateApiErrorMessage(raw, t)
      }

      if (!silent) {
        if (payload?.requiresApproval || error?.requiresApproval) {
          addNotif({ type: 'info', message: errorMessage, title: t('masterGovernance.pendingTitle'), duration: 10000 })
        } else if (['validation', '422'].includes(category)) {
          addNotif({ type: 'warning', message: errorMessage, title, duration: 10000 })
        } else {
          addNotif({ type: 'error', message: errorMessage, title, duration: 10000 })
        }
      }

      if (typeof onComplete === 'function') {
        onComplete(errorMessage, error)
      }

      return { errorMessage, category }
    },
    [t, categorize, addNotif]
  )

  const extractErrorMessages = useCallback((error: any): string[] => {
    const payload = error?.response?.data ?? error?.data
    if (payload?.errors) return formatErrorMessages(payload.errors).map(msg => translateApiErrorMessage(msg, t))
    if (error?.errors) return formatErrorMessages(error.errors).map(msg => translateApiErrorMessage(msg, t))
    if (error?.message) return [translateApiErrorMessage(error.message, t)]
    return []
  }, [t])

  const isValidationError = useCallback((error: any): boolean => {
    const payload = error?.response?.data ?? error?.data
    return !!(payload?.errors || error?.errors)
  }, [])

  const getFieldError = useCallback((error: any, fieldName: string): string | null => {
    const fieldErrors =
      error?.response?.data?.errors?.[fieldName] || error?.errors?.[fieldName]
    if (!fieldErrors) return null
    const rawError = Array.isArray(fieldErrors) ? fieldErrors[0] : String(fieldErrors)
    return translateApiErrorMessage(rawError, t)
  }, [t])

  return {
    notifications: [] as Notification[],
    showSuccess,
    showError,
    showWarning,
    showInfo,
    handleApiError,
    extractErrorMessages,
    isValidationError,
    getFieldError,
    categorize,
  }
}

function formatErrorMessages(errors: Record<string, any> | string): string[] {
  if (typeof errors === 'string') {
    return [errors]
  }
  if (typeof errors !== 'object' || errors === null) {
    return []
  }
  const formatted: string[] = []
  Object.keys(errors).forEach((key) => {
    // Domain responses carry machine metadata alongside the authored message.
    // These are not field validation failures (nor is a missing suggestion).
    if (key === 'rule' || key === 'suggestion') return
    const value = errors[key]
    if (value == null) return
    if (Array.isArray(value)) {
      formatted.push(...value)
    } else {
      formatted.push(String(value))
    }
  })
  return formatted
}

export { translateApiErrorMessage } from '@/utils/translateMessage'
